/* istanbul ignore file */
/**
 * Created by rathompson on 6/22/22
 * File: AdditionalOwnersForm
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useCallback, useContext, useState, EventHandler } from 'react'
import { Body, Bold, Button, ModalFooter, useModal } from '@panwds/react-ui'
import {
  BUTTON_CANCEL,
  BUTTON_ADD_OWNER,
  LABEL_OWNERS,
  LABEL_OWNERS_DESC,
  ERROR_ADD_OWNERS,
  PLACEHOLDER_ADD_OWNERS_INPUT,
  ERROR_INVALID_EMAIL
} from '../../../constants/LabelConstants'
import { Form, SubmitButton } from '@panwds/react-form'
import PillInput from '../../../../common/components/pillInput/PillInput'
import { DATA_TEST_OWNERS_CANCEL_BUTTON } from '../../../../common/constants/TestingDataIds'
import GlobalContext from '../../../../common/context/GlobalContext'
import AddOwnersModal from '../modal/AddOwnersModal'
import { IDP_OWNER_TYPES } from '../../../constants/IAMConstants'
import { CreateFederationOwner } from '../../../actions/IAMActions'
import isEmail from 'validator/lib/isEmail'
import ScreenLoader from '../../../../activation/components/common/ScreenLoader'

const Footer: VFC<{
  loading: boolean,
  onCancelPressed: EventHandler<any>,
  owners: any[],
  error: any,
}> = ({ loading, onCancelPressed, owners, error }) => {

  return (
    <ModalFooter addClassName='tw-absolute' style={{ right: 0, left: 0, bottom: 0, height: '43px' }} secondaryContent={
      <Button
        onClick={onCancelPressed}
        appearance="secondary"
        dataTestId={DATA_TEST_OWNERS_CANCEL_BUTTON}
        disabled={loading}>
        {BUTTON_CANCEL}
      </Button>
    }>
      <SubmitButton
        disabled={loading || !owners.length || error}
      >
        {BUTTON_ADD_OWNER}
      </SubmitButton>
    </ModalFooter>
  )
}

const AdditionalOwnersForm: VFC<{
  onCancelPressed: EventHandler<any>,
  domain: string,
  fetchData: Function
}> = ({ onCancelPressed, domain, fetchData }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<boolean>(false)
  const [apiError, setApiError] = useState<boolean>(false)
  const { closeModal } = useModal(AddOwnersModal)
  const [owners, setOwners] = useState([])
  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer

  const onSubmit = useCallback(async () => {
    const tsg_id = selectedTreeNode?.id

    if (tsg_id) {
      setApiError(false)
      setLoading(true)
      try {
        await Promise.all(owners?.map(async (owner) => {
          await CreateFederationOwner(tsg_id, domain, {
            user: owner,
            verification: { type: IDP_OWNER_TYPES.OWNER_GRANT }
          })
        }))
        await fetchData({ tsg_id, loading: false, enableSuccessBanner: true })
      }
      catch (error) {
        setApiError(true)
        closeModal()
      }
    }
    setLoading(false)
    closeModal()
  }, [selectedTreeNode?.id, closeModal, owners, domain, fetchData])

  return (
    <Form onSubmit={onSubmit} formProps={{ style: { height: '100%', width: '100%' } }}>
      {loading && <ScreenLoader />}
      <div className='tw-flex tw-flex-col tw-gap-2 tw-p-3'>
        <Bold>{LABEL_OWNERS}</Bold>
        <PillInput error={error} setError={setError} onChange={(owners: any) => setOwners(owners)} placeholder={PLACEHOLDER_ADD_OWNERS_INPUT} validate={isEmail} validationErrorMessage={ERROR_INVALID_EMAIL} />
        {apiError ? <Bold appearance='semibold' addClassName='tw-text-error'>{ERROR_ADD_OWNERS}</Bold> : <Body>{LABEL_OWNERS_DESC}</Body>}
      </div>
      <Footer {...{ loading, onCancelPressed, owners, error }}/>
    </Form>
  )
}

AdditionalOwnersForm.propTypes = {}
export default AdditionalOwnersForm

